import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  Globe,
  Loader2,
  Plus,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ExportButton } from "@/components/ui/ExportButton";
import { ColumnSelectorButton } from "@/components/ui/ColumnSelectorButton";
import { ViewToggleButton } from "@/components/ui/ViewToggleButton";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { RowActions } from "@/components/ui/RowActions";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { AppLayout } from "@/components/layout/AppLayout";
import { initials } from "@/lib/utils";
import { useCompanyList, useDeleteCompany } from "../hooks/useCompanies";
import { NewCompanyDrawer } from "../components/NewCompanyDrawer";
import { useViewMode } from "@/hooks/useViewMode";
import {
  useColumnVisibility,
  type ColumnDef,
} from "@/hooks/useColumnVisibility";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import type { CompanyCategory, CompanyListItem } from "../types";

type StatusFilter = "" | "PLANNED" | "ACTIVE" | "INACTIVE";
type TypeFilter = string;

const COMPANY_COLUMNS: ColumnDef[] = [
  { key: "name", label: "Name + Code" },
  { key: "type", label: "Type" },
  { key: "status", label: "Status" },
  { key: "currency", label: "Currency", defaultVisible: false },
  { key: "products", label: "Products" },
  { key: "projects", label: "Projects" },
];

// Color palette per dropdown value key. Falls back to a neutral chip for any
// custom (admin-added) type — Dropdown Manager doesn't carry a color, and
// the prompt rules out adding one to the schema for this phase.
const TYPE_BADGE_COLORS: Record<string, { bg: string; fg: string }> = {
  PARENT:     { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)" },
  SUBSIDIARY: { bg: "hsl(38,92%,92%)",  fg: "hsl(38,92%,30%)"   },
  PARTNER:    { bg: "hsl(262,55%,92%)", fg: "hsl(262,83%,38%)"  },
  CLIENT:     { bg: "hsl(175,55%,92%)", fg: "hsl(175,60%,30%)"  },
  CUSTOMER:   { bg: "hsl(340,55%,92%)", fg: "hsl(340,72%,38%)"  },
  VENDOR:     { bg: "hsl(142,55%,92%)", fg: "hsl(142,72%,28%)"  },
  INVESTOR:   { bg: "hsl(48,92%,92%)",  fg: "hsl(38,92%,30%)"   },
};

const NEUTRAL_BADGE = { bg: "hsl(210,30%,94%)", fg: "hsl(215,20%,35%)" };

function TypeBadge({
  type,
  labelOverride,
}: {
  type: string;
  labelOverride?: string;
}) {
  const c = TYPE_BADGE_COLORS[type] ?? NEUTRAL_BADGE;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={{ background: c.bg, color: c.fg }}
    >
      {labelOverride ?? type}
    </span>
  );
}

function TypeBadges({
  types,
  labelByValue,
}: {
  types: string[];
  labelByValue: Map<string, string>;
}) {
  if (types.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {types.map((t) => (
        <TypeBadge key={t} type={t} labelOverride={labelByValue.get(t)} />
      ))}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const active = status === "ACTIVE";
  const planned = status === "PLANNED";
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={
        active
          ? { background: "hsl(142,55%,92%)", color: "hsl(142,72%,28%)" }
          : planned
            ? { background: "hsl(210,80%,93%)", color: "hsl(210,80%,35%)" }
            : { background: "hsl(210,16%,93%)", color: "hsl(215,16%,40%)" }
      }
    >
      {active ? "Active" : planned ? "Planned" : "Inactive"}
    </span>
  );
}

function OperationalRow({
  company,
  onDelete,
  isVisible,
  labelByValue,
}: {
  company: CompanyListItem;
  onDelete: (id: string, name: string) => void;
  isVisible: (key: string) => boolean;
  labelByValue: Map<string, string>;
}) {
  const navigate = useNavigate();
  return (
    <tr
      className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
      onClick={() => navigate(`/companies/${company.id}`)}
    >
      {isVisible("name") && (
        <td className="px-6 py-4">
          <div className="font-medium text-foreground">{company.name}</div>
          <div className="mt-0.5 font-mono text-xs text-muted-foreground">
            {company.code}
          </div>
        </td>
      )}
      {isVisible("type") && (
        <td className="px-6 py-4">
          <TypeBadges types={company.types} labelByValue={labelByValue} />
        </td>
      )}
      {isVisible("status") && (
        <td className="px-6 py-4">
          <StatusBadge status={company.status} />
        </td>
      )}
      {isVisible("currency") && (
        <td className="px-6 py-4 text-sm text-muted-foreground">
          {company.currency}
        </td>
      )}
      {isVisible("products") && (
        <td className="px-6 py-4 text-sm text-muted-foreground">
          {company._count.products}
        </td>
      )}
      {isVisible("projects") && (
        <td className="px-6 py-4 text-sm text-muted-foreground">
          {company._count.projects}
        </td>
      )}
      <td className="px-6 py-4">
        <RowActions
          onView={() => navigate(`/companies/${company.id}`)}
          onEdit={() => navigate(`/companies/${company.id}?edit=true`)}
          onDelete={() => onDelete(company.id, company.name)}
        />
      </td>
    </tr>
  );
}

function ExternalRow({
  company,
  onDelete,
  labelByValue,
}: {
  company: CompanyListItem;
  onDelete: (id: string, name: string) => void;
  labelByValue: Map<string, string>;
}) {
  const navigate = useNavigate();
  return (
    <tr
      className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
      onClick={() => navigate(`/companies/${company.id}`)}
    >
      <td className="px-6 py-4">
        <div className="font-medium text-foreground">{company.name}</div>
        <div className="mt-0.5 font-mono text-xs text-muted-foreground">
          {company.code}
        </div>
      </td>
      <td className="px-6 py-4">
        <TypeBadges types={company.types} labelByValue={labelByValue} />
      </td>
      <td className="px-6 py-4">
        <StatusBadge status={company.status} />
      </td>
      <td className="px-6 py-4 text-sm text-muted-foreground">—</td>
      <td className="px-6 py-4 text-sm text-muted-foreground">
        {company._count.contacts ?? 0}
      </td>
      <td className="px-6 py-4">
        <RowActions
          onView={() => navigate(`/companies/${company.id}`)}
          onEdit={() => navigate(`/companies/${company.id}?edit=true`)}
          onDelete={() => onDelete(company.id, company.name)}
        />
      </td>
    </tr>
  );
}

function CompanyCard({
  company,
  labelByValue,
}: {
  company: CompanyListItem;
  labelByValue: Map<string, string>;
}) {
  const navigate = useNavigate();
  return (
    <div
      className="cursor-pointer rounded-xl border bg-card p-5 transition-colors hover:bg-[hsl(210,50%,98%)]"
      style={{ borderColor: "hsl(210,30%,85%)" }}
      onClick={() => navigate(`/companies/${company.id}`)}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[hsl(210,55%,92%)] text-sm font-semibold text-[hsl(210,100%,28%)]">
          {initials(company.name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-foreground">{company.name}</div>
          <div className="mt-0.5 font-mono text-xs text-muted-foreground">
            {company.code}
          </div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <TypeBadges types={company.types} labelByValue={labelByValue} />
        <StatusBadge status={company.status} />
      </div>
      <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
        <span>
          <span className="font-medium text-foreground">
            {company._count.products}
          </span>{" "}
          Products
        </span>
        <span>
          <span className="font-medium text-foreground">
            {company._count.projects}
          </span>{" "}
          Projects
        </span>
      </div>
    </div>
  );
}

type CompanyTab = "operational" | "external";

const TAB_TO_CATEGORY: Record<CompanyTab, CompanyCategory> = {
  operational: "OPERATIONAL",
  external: "EXTERNAL",
};

export default function CompanyListPage() {
  const [tab, setTab] = useState<CompanyTab>("operational");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("");
  // External companies are shown one type at a time, as tabs. null = not yet
  // chosen, which opens on Client; "" = All.
  const [externalType, setExternalType] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);

  // All company_type values, used to populate the Type filter and to look up
  // labels for the Type column. One query, cached by react-query.
  const { data: allTypes = [] } = useCategoryItems("company_type");
  const typeLabelByValue = new Map(allTypes.map((t) => [t.value, t.label]));
  const allowedTypes = allTypes.filter(
    (t) =>
      t.isActive &&
      typeof t.metadata?.category === "string" &&
      t.metadata.category === TAB_TO_CATEGORY[tab],
  );
  const externalTypes = allTypes.filter(
    (t) => t.isActive && typeof t.metadata?.category === "string" && t.metadata.category === "EXTERNAL",
  );
  const defaultExternalType =
    externalTypes.find((t) => t.value === "CLIENT")?.value ?? externalTypes[0]?.value ?? "";
  const activeType = tab === "external" ? (externalType ?? defaultExternalType) : typeFilter;
  const { mode: viewMode, toggle: toggleViewMode, isGrid } = useViewMode(
    "companies",
    "list",
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const {
    visibility,
    toggle: toggleColumn,
    reset: resetColumns,
    visibleKeys,
    isVisible,
    minReached,
  } = useColumnVisibility("companies", COMPANY_COLUMNS);
  const exportFilters = useMemo(
    () => ({
      search: search || undefined,
      status: statusFilter || undefined,
      type: activeType || undefined,
      tab,
    }),
    [search, statusFilter, activeType, tab],
  );

  const deleteCompany = useDeleteCompany();

  const {
    data: result,
    isLoading,
    isError,
    error,
  } = useCompanyList({
    page,
    limit,
    search: search.trim() || undefined,
    status: statusFilter || undefined,
    type: activeType || undefined,
    tab,
  });

  const meta = result?.meta;
  const operationalCount = meta?.operationalCount ?? 0;
  const externalCount = meta?.externalCount ?? 0;

  function switchTab(next: CompanyTab) {
    if (next === tab) return;
    setTab(next);
    setPage(1);
    setTypeFilter("");
    setExternalType(null);
  }

  const companies = result?.data ?? [];
  const pagination = result?.pagination ?? {
    page: 1,
    limit,
    total: 0,
    totalPages: 1,
  };
  const total = pagination.total;

  function resetPage<T>(setter: (v: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(1);
    };
  }

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  function handleDelete(id: string, name: string) {
    setDeleteTarget({ id, name });
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    await deleteCompany.mutateAsync(deleteTarget.id);
    showToast(`${deleteTarget.name} deleted successfully`);
  }

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Business Structure" },
        { label: "Companies" },
      ]}
    >
      <div className="w-full space-y-6">
        {/* Toast */}
        {toast && (
          <div
            className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg"
            style={{ borderColor: "hsl(142,55%,80%)" }}
          >
            {toast}
          </div>
        )}

        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2
              className="h-[18px] w-[18px] text-foreground"
              strokeWidth={1.5}
            />
            <h1 className="text-2xl font-semibold tracking-tight">Companies</h1>
          </div>
          <div className="flex items-center gap-2">
            <ViewToggleButton mode={viewMode} onToggle={toggleViewMode} />
            <ExportButton
              module="companies"
              filters={exportFilters}
              visibleColumns={visibleKeys}
            />
            <ColumnSelectorButton
              columns={COMPANY_COLUMNS}
              visibility={visibility}
              onToggle={toggleColumn}
              onReset={resetColumns}
              minReached={minReached}
            />
            <Button onClick={() => setDrawerOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              {tab === "external" ? "New External Company" : "New Company"}
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div
          className="flex gap-0"
          style={{
            borderBottom: "1px solid hsl(210,30%,85%)",
            marginBottom: 16,
          }}
        >
          {(
            [
              {
                key: "operational" as CompanyTab,
                label: "Our Companies",
                icon: Building2,
                count: operationalCount,
              },
              {
                key: "external" as CompanyTab,
                label: "External",
                icon: Globe,
                count: externalCount,
              },
            ]
          ).map(({ key, label, icon: Icon, count }) => {
            const active = tab === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => switchTab(key)}
                style={{
                  padding: "8px 16px",
                  fontSize: 13,
                  fontWeight: 500,
                  color: active
                    ? "hsl(210,68%,55%)"
                    : "hsl(215,16%,47%)",
                  cursor: "pointer",
                  borderBottom: active
                    ? "2px solid hsl(210,68%,55%)"
                    : "2px solid transparent",
                  marginBottom: -1,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  transition: "all 0.12s",
                  background: "transparent",
                }}
              >
                <Icon className="h-[14px] w-[14px]" strokeWidth={1.5} />
                {label} ({count})
              </button>
            );
          })}
        </div>

        {tab === "external" && externalTypes.length > 0 && (
          <div
            className="flex gap-0"
            style={{ borderBottom: "1px solid hsl(210,30%,90%)", marginTop: -8, marginBottom: 16 }}
          >
            {[...externalTypes.map((t) => ({ value: t.value, label: t.label })), { value: "", label: "All" }].map(
              ({ value, label }) => {
                const active = activeType === value;
                return (
                  <button
                    key={value || "all"}
                    type="button"
                    onClick={() => {
                      setExternalType(value);
                      setPage(1);
                    }}
                    style={{
                      padding: "6px 14px",
                      fontSize: 12.5,
                      fontWeight: 500,
                      color: active ? "hsl(210,68%,55%)" : "hsl(215,16%,47%)",
                      cursor: "pointer",
                      borderBottom: active ? "2px solid hsl(210,68%,55%)" : "2px solid transparent",
                      marginBottom: -1,
                      transition: "all 0.12s",
                      background: "transparent",
                    }}
                  >
                    {label}
                  </button>
                );
              },
            )}
          </div>
        )}

        {isError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
            <div className="font-medium">Failed to load companies</div>
            <div className="mt-1 font-mono text-xs opacity-80">
              {(error as Error)?.message ?? "Unknown error"}
            </div>
          </div>
        ) : (
          <>
            {/* Filters */}
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <div className="relative w-[280px]">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <Input
                  placeholder="Search companies..."
                  value={search}
                  onChange={(e) => resetPage(setSearch)(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select
                value={statusFilter}
                onChange={(e) =>
                  resetPage(setStatusFilter)(e.target.value as StatusFilter)
                }
                className="w-40"
              >
                <option value="">All Status</option>
                <option value="PLANNED">Planned</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </Select>
              {tab === "operational" && (
                <Select
                  value={typeFilter}
                  onChange={(e) =>
                    resetPage(setTypeFilter)(e.target.value)
                  }
                  className="w-40"
                >
                  <option value="">All Types</option>
                  {allowedTypes.map((t) => (
                    <option key={t.id} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Select>
              )}
            </div>

            {/* Grid view */}
            {isGrid ? (
              <div>
                {isLoading ? (
                  <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                    <Loader2
                      className="mr-2 h-4 w-4 animate-spin"
                      strokeWidth={1.5}
                    />
                    Loading companies…
                  </div>
                ) : companies.length === 0 ? (
                  <div className="py-16 text-center text-sm text-muted-foreground">
                    No companies match the current filters.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {companies.map((c) => (
                      <CompanyCard
                        key={c.id}
                        company={c}
                        labelByValue={typeLabelByValue}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* Table view */
              <div
                className="overflow-hidden rounded-xl border bg-card"
                style={{ borderColor: "hsl(210,30%,85%)" }}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                    <Loader2
                      className="mr-2 h-4 w-4 animate-spin"
                      strokeWidth={1.5}
                    />
                    Loading companies…
                  </div>
                ) : companies.length === 0 ? (
                  <div className="py-16 text-center text-sm text-muted-foreground">
                    No companies match the current filters.
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {tab === "operational" ? (
                        <tr>
                          {isVisible("name") && (
                            <th className="px-6 py-3">Name + Code</th>
                          )}
                          {isVisible("type") && (
                            <th className="px-6 py-3">Type</th>
                          )}
                          {isVisible("status") && (
                            <th className="px-6 py-3">Status</th>
                          )}
                          {isVisible("currency") && (
                            <th className="px-6 py-3">Currency</th>
                          )}
                          {isVisible("products") && (
                            <th className="px-6 py-3">Products</th>
                          )}
                          {isVisible("projects") && (
                            <th className="px-6 py-3">Projects</th>
                          )}
                          <th className="px-6 py-3 text-center">Actions</th>
                        </tr>
                      ) : (
                        <tr>
                          <th className="px-6 py-3">Name + Code</th>
                          <th className="px-6 py-3">Type</th>
                          <th className="px-6 py-3">Status</th>
                          <th className="px-6 py-3">Primary Contact</th>
                          <th className="px-6 py-3">Contacts</th>
                          <th className="px-6 py-3 text-center">Actions</th>
                        </tr>
                      )}
                    </thead>
                    <tbody className="divide-y divide-border">
                      {companies.map((c) =>
                        tab === "operational" ? (
                          <OperationalRow
                            key={c.id}
                            company={c}
                            onDelete={handleDelete}
                            isVisible={isVisible}
                            labelByValue={typeLabelByValue}
                          />
                        ) : (
                          <ExternalRow
                            key={c.id}
                            company={c}
                            onDelete={handleDelete}
                            labelByValue={typeLabelByValue}
                          />
                        ),
                      )}
                    </tbody>
                  </table>
                )}

                {/* Pagination footer */}
                <div data-pagination="true" style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '9px 14px',
                  borderTop: '1px solid hsl(210,30%,85%)',
                  background: 'hsl(210,30%,97%)',
                  fontSize: '12px',
                  color: 'hsl(215,16%,47%)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>
                      Showing {total === 0 ? 0 : (page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total} entries
                    </span>
                    <span style={{ marginLeft: '8px' }}>Show</span>
                    <select
                      value={limit}
                      onChange={e => { setLimit(Number(e.target.value)); setPage(1) }}
                      style={{
                        height: '26px',
                        padding: '0 4px',
                        border: '1px solid hsl(210,30%,85%)',
                        borderRadius: '4px',
                        fontSize: '12px',
                        background: 'white',
                        cursor: 'pointer',
                      }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span>entries</span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <button
                      onClick={() => setPage(p => p - 1)}
                      disabled={page === 1}
                      style={{
                        width: '28px', height: '28px',
                        border: '1px solid hsl(210,30%,85%)',
                        borderRadius: '4px',
                        background: 'white',
                        cursor: page === 1 ? 'not-allowed' : 'pointer',
                        opacity: page === 1 ? 0.4 : 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >‹</button>
                    <span style={{ padding: '0 10px' }}>
                      {Math.ceil(total / limit) === 0 ? '0' : page} / {Math.ceil(total / limit) || 1}
                    </span>
                    <button
                      onClick={() => setPage(p => p + 1)}
                      disabled={page >= Math.ceil(total / limit)}
                      style={{
                        width: '28px', height: '28px',
                        border: '1px solid hsl(210,30%,85%)',
                        borderRadius: '4px',
                        background: 'white',
                        cursor: page >= Math.ceil(total / limit) ? 'not-allowed' : 'pointer',
                        opacity: page >= Math.ceil(total / limit) ? 0.4 : 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >›</button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        <NewCompanyDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          category={TAB_TO_CATEGORY[tab]}
          defaultType={tab === "external" ? activeType || undefined : undefined}
          titleOverride={
            tab === "external" ? "New External Company" : "New Company"
          }
        />

        <ConfirmDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete company?"
          description={
            deleteTarget
              ? `Are you sure you want to delete '${deleteTarget.name}'? It will be removed from your Companies list.`
              : ""
          }
          onConfirm={confirmDelete}
        />
      </div>
    </AppLayout>
  );
}
